import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../utils/apiClient";
import { Grid, Paper, Typography, Box, CircularProgress } from "@mui/material";
import { Gavel } from "@mui/icons-material";
import PageLayout from "../components/admin/PageLayout";

// Committee types we support. Add more here (e.g. fixed, chit, etc.) when you add those flows.
const COMMITTEE_TYPES = [
  {
    id: "auction",
    label: "Auction Committees",
    path: "/committees/auction",
    icon: <Gavel sx={{ fontSize: 28 }} />,
    color: "primary.main",
  },
  // Future: { id: "fixed", label: "Fixed Committees", path: "/committees/fixed", icon: <...>, color: "secondary.main" },
];

const TypeCard = ({ label, count, icon, color, path, onClick }) => (
  <Paper
    component="button"
    type="button"
    elevation={0}
    onClick={() => (onClick ? onClick() : null)}
    sx={{
      width: "100%",
      p: 3,
      borderRadius: 2,
      border: "1px solid",
      borderColor: "divider",
      cursor: "pointer",
      textAlign: "left",
      bgcolor: "background.paper",
      transition: "box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease",
      "&:hover": {
        boxShadow: "0px 8px 24px rgba(94, 53, 177, 0.12)",
        transform: "translateY(-2px)",
        borderColor: "primary.light",
      },
    }}
  >
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: 2,
        bgcolor: `${color}20`,
        color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mb: 2,
      }}
    >
      {icon}
    </Box>
    <Typography variant="body2" color="text.secondary" fontWeight={500}>
      {label}
    </Typography>
    <Typography variant="h4" fontWeight={700} color="primary.main" sx={{ mt: 0.5 }}>
      {count}
    </Typography>
  </Paper>
);

const CommitteesOverview = () => {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        setLoading(true);
        const next = {};
        for (const t of COMMITTEE_TYPES) {
          const res = await apiClient.get("committee/count", { params: { type: t.id } });
          next[t.id] = res.data?.data ?? 0;
        }
        setCounts(next);
      } catch (e) {
        console.error("Error fetching committee counts:", e);
        setCounts(Object.fromEntries(COMMITTEE_TYPES.map((t) => [t.id, 0])));
      } finally {
        setLoading(false);
      }
    };
    fetchCounts();
  }, []);

  return (
    <PageLayout
      title="Committees"
      subtitle="Manage committees by type. Click a card to view and manage that type."
    >
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={280}>
          <CircularProgress size={48} sx={{ color: "primary.main" }} />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {COMMITTEE_TYPES.map((t) => (
            <Grid item xs={12} sm={6} md={4} key={t.id}>
              <TypeCard
                label={t.label}
                count={counts[t.id] ?? 0}
                icon={t.icon}
                color={t.color}
                path={t.path}
                onClick={() => navigate(t.path)}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </PageLayout>
  );
};

export default CommitteesOverview;
